import type { CoachingSession } from '@efdigital/ll-addon-data';
import type { LookupUser } from '@efdigital/ll-admin-v2/services/user-lookup';
import {
  participationReportLabel,
  resolvedOutcomeLabel,
} from '@efdigital/ll-admin-v2/utils/coaching/coaching-sessions/resolve-outcomes';
import { displayValue } from '@efdigital/ll-admin-v2/utils/display-value';
import { userDisplayName } from '@efdigital/ll-admin-v2/utils/users';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import { SessionResolveModal } from './session-resolve-modal.gts';

export default class SessionParticipation extends Component<{
  Args: {
    coachee: LookupUser;
    coach: LookupUser;
    coachingSession: CoachingSession;
    onSuccess?: () => Promise<void>;
  };
  Element: HTMLDivElement;
}> {
  get needsResolution() {
    return this.args.coachingSession.status === 'awaiting_attendance_review';
  }

  <template>
    <div class="card rounded-4" ...attributes>
      <h3 class="card-header h5 py-3">Attendance</h3>
      <div class="card-body">
        <div class="row">

          <div class="col-12 col-xl-4">
            {{#if @coachingSession.resolvedOutcome}}
              <h4 class="gud-eyebrow">Resolved outcome</h4>
              <div class="mb-4">
                {{resolvedOutcomeLabel @coachingSession.resolvedOutcome}}
              </div>
            {{else}}
              {{#if this.needsResolution}}
                <div
                  class="alert alert-warning d-flex align-items-start gap-2 mb-4"
                  role="alert"
                  data-test-session-alert="awaiting-attendance-review"
                >
                  <iconify-icon
                    icon="ph:warning"
                    class="fs-4"
                    aria-hidden="true"
                  />
                  <p class="mb-0 lh-sm">
                    The participation reports are conflicting or require further
                    clarification. Please select a resolved outcome below. If
                    not resolved within 7 days, the system will automatically
                    close the session with a default outcome of
                    <em>no show by both</em>.
                  </p>
                </div>

                <SessionResolveModal
                  @coachingSession={{@coachingSession}}
                  @onSuccess={{@onSuccess}}
                >
                  <:resolve as |showModal|>
                    <button
                      type="button"
                      class="btn btn-warning"
                      {{on "click" showModal}}
                    >Resolve attendance...</button>
                  </:resolve>
                </SessionResolveModal>
              {{/if}}
            {{/if}}
          </div>

          <div class="col-12 col-xl-4">
            <h4 class="gud-eyebrow">Coachee</h4>
            <div class="mb-4">
              {{userDisplayName @coachee}}
            </div>
            <div class="mb-4">
              <div class="form-label text-body-secondary mb-0">Report</div>
              <div>
                {{#if @coachingSession.coacheeReport}}
                  {{participationReportLabel @coachingSession.coacheeReport}}
                {{else}}
                  —
                {{/if}}
              </div>
            </div>
            <div>
              <div class="form-label text-body-secondary mb-0">Notes</div>
              <div>
                {{displayValue @coachingSession.coacheeReportedIssueDetails}}
              </div>
            </div>
          </div>

          <div class="col-12 col-xl-4">
            <h4 class="gud-eyebrow">Coach</h4>
            <div class="mb-4">
              {{userDisplayName @coach}}
            </div>
            <div class="mb-4">
              <div class="form-label text-body-secondary mb-0">Report</div>
              <div>
                {{#if @coachingSession.coachReport}}
                  {{participationReportLabel @coachingSession.coachReport}}
                {{else}}
                  —
                {{/if}}
              </div>
            </div>
            <div>
              <div class="form-label text-body-secondary mb-0">Notes</div>
              <div>
                {{displayValue @coachingSession.coachReportedIssueDetails}}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
}
